import React, { useState } from 'react';

import clsx from 'clsx';
import PropTypes from 'prop-types';

import { Mui } from 'components';

const FILTER_INPUT_HEIGHT = 40;

const useStyles = Mui.makeStyles(theme => ({
  clickable: { '&:hover': { cursor: 'pointer' } },
  action: { marginTop: 0 },
  avatar: {
    backgroundColor: ({ active }) =>
      active ? theme.palette.primary.main : theme.palette.grey[600],
  },
  content: {
    '& .MuiFormControl-root': {
      marginTop: 0,
    },
    '& .MuiOutlinedInput-root': {
      minHeight: FILTER_INPUT_HEIGHT,
    },
    '& .MuiOutlinedInput-input:not(.MuiAutocomplete-input)': {
      paddingTop: 10.5,
      paddingBottom: 10.5,
    },
    '& .MuiSelect-outlined': {
      paddingTop: 10.5,
      paddingBottom: 10.5,
    },
    '& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
      transform: 'translate(14px, 12px) scale(1)',
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      minHeight: FILTER_INPUT_HEIGHT,
      paddingTop: 2,
      paddingBottom: 2,
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input': {
      padding: '7.5px 4px',
      width: 'auto',
      flexGrow: 1,
      minWidth: 30,
      opacity: 1,
    },
    '& .MuiChip-root': {
      height: 24,
    },
    '& .MuiFormHelperText-root:empty': {
      display: 'none',
      margin: 0,
    },
  },
  fullHeightCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  fullHeightCollapse: {
    flex: '1 1 auto',
    '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner, & .MuiCardContent-root': {
      height: '100%',
    },
  },
  fullHeightContent: {
    flex: '1 1 auto',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
  },
  footer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: 'auto',
    paddingTop: theme.spacing(3),
  },
}));

function CollapsibleFilters({
  active = false,
  children,
  clearLabel = 'Limpar filtros',
  defaultExpanded = false,
  footer,
  fullHeight = false,
  onClear,
  title = 'Filtros',
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const classes = useStyles({ active });

  const collapsible = !defaultExpanded;
  const handleToggle = collapsible ? () => setExpanded(prev => !prev) : undefined;

  const content = (
    <Mui.CardContent className={clsx(classes.content, fullHeight && classes.fullHeightContent)}>
      {children}
      {!!footer && <div className={classes.footer}>{footer}</div>}
    </Mui.CardContent>
  );

  return (
    <Mui.Card variant="outlined" className={fullHeight ? classes.fullHeightCard : undefined}>
      <Mui.CardHeader
        disableTypography
        title={
          <Mui.Box display="flex" alignItems="center" gridGap={10}>
            <Mui.Typography
              variant="h5"
              onClick={handleToggle}
              className={collapsible ? classes.clickable : undefined}>
              {title}
            </Mui.Typography>
            {active && !!onClear && (
              <Mui.Button
                color="default"
                size="small"
                variant="outlined"
                onClick={onClear}
                endIcon={<Mui.Icon fontSize="small">clear</Mui.Icon>}>
                {clearLabel}
              </Mui.Button>
            )}
          </Mui.Box>
        }
        avatar={
          <Mui.Avatar
            onClick={handleToggle}
            className={clsx(classes.avatar, collapsible && classes.clickable)}>
            <Mui.Icon>filter_list</Mui.Icon>
          </Mui.Avatar>
        }
        action={
          collapsible && (
            <Mui.IconButton onClick={handleToggle}>
              <Mui.Icon>{expanded ? 'expand_less' : 'expand_more'}</Mui.Icon>
            </Mui.IconButton>
          )
        }
        classes={{ action: classes.action }}
      />
      {collapsible ? (
        <Mui.Collapse
          in={expanded}
          mountOnEnter
          className={fullHeight ? classes.fullHeightCollapse : undefined}>
          {content}
        </Mui.Collapse>
      ) : (
        content
      )}
    </Mui.Card>
  );
}

CollapsibleFilters.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  clearLabel: PropTypes.string,
  defaultExpanded: PropTypes.bool,
  footer: PropTypes.node,
  fullHeight: PropTypes.bool,
  onClear: PropTypes.func,
  title: PropTypes.string,
};

export default CollapsibleFilters;
